import { useRouteOrganizationId } from '@/hooks/useRouteOrganizationId'
import queryClient from '@/lib/queryClient'
import { buildAdminQueryRoot, type OrganizationIdInput } from '@/lib/organization-scoping'
import { collectionQueryKeys } from '@/lib/services/collections/collections.query'
import { productQueryKeys } from '@/lib/services/products/products.query'
import { queryOptions, useMutation, useQuery, useSuspenseQuery } from '@tanstack/react-query'
import {
  createAddOnSet,
  deleteAddOnSet,
  getAddOnSet,
  getAddOnSets,
  updateAddOnSet,
  type AddOnSetIndexScope,
  type AddOnSetScope,
} from './addOnSets.api'
import { addOnSetQueryKeys } from './addOnSets.keys'
import type { AddOnSetIdInput, AddOnSetInput, UpdateAddOnSetInput } from './addOnSets.types'

export { addOnSetQueryKeys }

/** Hooks fall back to the route's organization. */
interface OrganizationScope {
  organizationId?: OrganizationIdInput
}

/** Query options a caller may override; the key and fetcher stay owned by this module. */
type QueryParams<TOptions> = Partial<Omit<TOptions, 'queryKey' | 'queryFn'>>

/** The update mutation's variables: the set to change and its new values. */
export type UpdateAddOnSetValues = UpdateAddOnSetInput & { addOnSetId: AddOnSetIdInput }

export const addOnSetQueries = {
  index: ({ organizationId, filters }: AddOnSetIndexScope) =>
    queryOptions({
      queryKey: addOnSetQueryKeys.index({ organizationId, filters }),
      queryFn: getAddOnSets,
    }),
  show: ({ organizationId, addOnSetId }: AddOnSetScope) =>
    queryOptions({
      queryKey: addOnSetQueryKeys.show({ organizationId, addOnSetId }),
      queryFn: getAddOnSet,
    }),
}

export function useAddOnSets({
  organizationId,
  filters,
  params = {},
}: OrganizationScope &
  Pick<AddOnSetIndexScope, 'filters'> & {
    params?: QueryParams<ReturnType<typeof addOnSetQueries.index>>
  } = {}) {
  const resolvedOrganizationId = useRouteOrganizationId(organizationId)

  return useQuery({
    ...addOnSetQueries.index({ organizationId: resolvedOrganizationId, filters }),
    ...params,
  })
}

export function useAddOnSet(
  addOnSetId: AddOnSetIdInput,
  {
    organizationId,
    ...params
  }: OrganizationScope & QueryParams<ReturnType<typeof addOnSetQueries.show>> = {}
) {
  const resolvedOrganizationId = useRouteOrganizationId(organizationId)

  return useSuspenseQuery({
    ...addOnSetQueries.show({ organizationId: resolvedOrganizationId, addOnSetId }),
    ...params,
  })
}

function invalidateAddOnSetDependencies(organizationId: number) {
  const addOnRootQueryKey = [buildAdminQueryRoot({ entity: 'productAddOn' }, { organizationId })]

  return Promise.all([
    queryClient.invalidateQueries({ queryKey: addOnSetQueryKeys.all({ organizationId }) }),
    queryClient.invalidateQueries({ queryKey: addOnRootQueryKey }),
    queryClient.invalidateQueries({ queryKey: productQueryKeys.all({ organizationId }) }),
    queryClient.invalidateQueries({ queryKey: collectionQueryKeys.all({ organizationId }) }),
  ])
}

export function useCreateAddOnSetMutation() {
  const organizationId = useRouteOrganizationId()

  return useMutation({
    mutationFn: (values: AddOnSetInput) => createAddOnSet({ organizationId, ...values }),
    onSuccess: () => invalidateAddOnSetDependencies(organizationId),
  })
}

export function useUpdateAddOnSetMutation() {
  const organizationId = useRouteOrganizationId()

  return useMutation({
    mutationFn: (values: UpdateAddOnSetValues) => updateAddOnSet({ organizationId, ...values }),
    onSuccess: (_, values) =>
      Promise.all([
        invalidateAddOnSetDependencies(organizationId),
        queryClient.invalidateQueries({
          queryKey: addOnSetQueryKeys.show({ organizationId, addOnSetId: values.addOnSetId }),
        }),
      ]),
  })
}

export function useDeleteAddOnSetMutation() {
  const organizationId = useRouteOrganizationId()

  return useMutation({
    mutationFn: (addOnSetId: AddOnSetIdInput) => deleteAddOnSet({ organizationId, addOnSetId }),
    onSuccess: (_, addOnSetId) => {
      queryClient.removeQueries({
        queryKey: addOnSetQueryKeys.show({ organizationId, addOnSetId }),
      })
      return invalidateAddOnSetDependencies(organizationId)
    },
  })
}
